import { useMemo } from 'react';
import type { BackgroundValue } from '@elfsight/configurator-contracts';
import { isDarkColor } from '@elfsight/lib-utils';
import type { BlockType } from '@elfsight/widget-banner/src/types';
import type { blockSettings } from '@elfsight/widget-banner/widget';
import { isTransparentBackground } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';

type BlockSettingsMap = typeof blockSettings;
type BlockSettings = {
  [K in keyof BlockSettingsMap]: {
    type: K;
  } & BlockSettingsMap[K];
}[keyof BlockSettingsMap];

interface ThumbnailStyles {
  margin: number;
  boxShadow: string;
  borderRadius: number;
  overflow: string;
  backgroundColor?: string;
  height?: string;
  maxWidth?: string;
  minWidth?: string;
}

const BASE_STYLE: ThumbnailStyles = {
  margin: 50,
  boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.1)',
  borderRadius: 24,
  overflow: 'hidden'
};

const PRIORITY_BLOCKS: BlockType[] = ['heading', 'text', 'coupon', 'form'];

const LIGHT_BG = '#fff';
const DARK_BG = '#111';

const getContrastBackground = (
  widgetBackground: BackgroundValue,
  blocks?: BlockSettings[]
) => {
  if (!isTransparentBackground(widgetBackground) || !blocks?.length) {
    return;
  }

  for (const blockType of PRIORITY_BLOCKS) {
    const targetBlock = blocks.find((block) => block.type === blockType);

    if (!targetBlock) {
      continue;
    }

    if (targetBlock.type === 'form') {
      const isLabelInside = targetBlock.fieldLabelPosition === 'inside';
      const isDark = isLabelInside
        ? isDarkColor(targetBlock.fieldInsideLabelColor)
        : isDarkColor(targetBlock.fieldLabelColor);

      return isDark ? LIGHT_BG : DARK_BG;
    }

    if ('color' in targetBlock) {
      return isDarkColor(targetBlock.color) ? LIGHT_BG : DARK_BG;
    }
  }

  return;
};

const getThumbnailStyles = (
  withHeight: boolean,
  withWidth: boolean,
  backgroundColor?: string,
  enabled?: boolean
): Partial<ThumbnailStyles> => {
  if (!enabled) {
    return {};
  }

  const styles: ThumbnailStyles = { ...BASE_STYLE };

  if (backgroundColor) {
    styles.backgroundColor = backgroundColor;
  }

  if (withHeight) {
    styles.height = 'calc(100% - 100px)';
  }

  if (withWidth) {
    styles.maxWidth = 'calc(100vw - 100px)';
    styles.minWidth = 'unset';
  }

  return styles;
};

export const useThumbnailMode = ({
  withHeight = false,
  withWidth = false
}: {
  withHeight?: boolean;
  withWidth?: boolean;
} = {}) => {
  const {
    renderThumbnailMode: thumbnailMode,
    widgetBackground,
    blocks
  } = useSettings();

  const styles = useMemo(() => {
    const backgroundColor = getContrastBackground(widgetBackground, blocks);
    return getThumbnailStyles(
      withHeight,
      withWidth,
      backgroundColor,
      thumbnailMode
    );
  }, [thumbnailMode, withHeight, withWidth, widgetBackground, blocks]);

  return { thumbnailMode, styles };
};
